/**
 * Flip carry & profit panel — the Properties-section counterpart to the rental
 * "Property Cash Flow" panel, for a fix-and-flip.
 *
 * A flip is not an income statement: it is buy → rehab → carry → sell, with cash
 * arriving only once, at the sale. So instead of rent / operating-expense /
 * net-cash-flow this panel shows:
 *   - Deal economics from `computeFlip` (Est./realized profit · ROI · margin ·
 *     total holding · selling costs).
 *   - A month-by-month HOLDING-COST timeline (actuals): debt carry + logged
 *     carrying costs + one-time rehab spend, with a running "cash in deal".
 *   - A sale event (proceeds − selling costs − loan payoff = net) once sold.
 *
 * Sale proceeds default to ARV until a stored sold price exists.
 */
import { useState } from "react";
import { ArrowLeftRight, Plus } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { fmtMoney, fmtPct, type FlipResult } from "@/lib/calculators";
import type { YearMonthWithLogged } from "@/components/properties/cashflow-detail";

interface PropertyFlipPanelProps {
  year: number;
  years: number[];
  onYearChange: (year: number) => void;
  /** The year's months (flip = phase "rehab" / "hold" for owned months). */
  months: YearMonthWithLogged[];
  /** Deal-level flip economics from `computeFlip`. */
  flip: FlipResult;
  /** Sale price (defaults to ARV until a stored sold price exists). */
  arv: number;
  /** True once the property is marked sold — realizes the sale event. */
  isSold: boolean;
  /** Remaining loan principal paid off at sale (≈ HML loan amount). */
  loanPayoff: number;
  onAddExpense: (month: YearMonthWithLogged) => void;
  onViewBreakdown: (month: YearMonthWithLogged) => void;
}

/**
 * A flip holding month has no income — its cash burn is the modeled net (debt +
 * per-month holding estimate + logged carry + rehab). The holding is already
 * baked into `breakdown.net` per-month (sourced from settings for the current
 * month, from the frozen log for past months), so this is simply −net.
 */
function monthBurn(m: YearMonthWithLogged): number {
  return m.breakdown || m.rehabSpend > 0 ? Math.max(0, -m.netWithLogged) : 0;
}

/** Owned (rehab/hold) months only — the flip's carry window. */
function isOwned(m: YearMonthWithLogged): boolean {
  return m.phase === "rehab" || m.phase === "hold";
}

export function PropertyFlipPanel({
  year,
  years,
  onYearChange,
  months,
  flip,
  arv,
  isSold,
  loanPayoff,
  onAddExpense,
  onViewBreakdown,
}: PropertyFlipPanelProps) {
  // Default to the live month, else the last owned month with figures.
  const defaultIdx = (() => {
    const live = months.findIndex((m) => m.source === "live");
    if (live >= 0) return live;
    const owned = months.map((m, i) => (isOwned(m) ? i : -1)).filter((i) => i >= 0);
    return owned.length ? owned[owned.length - 1] : 0;
  })();
  const [selected, setSelected] = useState(defaultIdx);
  const active = months[Math.min(selected, months.length - 1)] ?? months[0];

  const holdingToDate = months.reduce((s, m) => s + monthBurn(m), 0);
  const netProceeds = arv - flip.selling - loanPayoff;

  return (
    <Card className="space-y-4 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <ArrowLeftRight className="h-4 w-4 text-primary" aria-hidden />
            Flip Carry &amp; Profit
          </h2>
          <p className="text-xs text-muted-foreground">
            Holding costs while carried, and the profit realized at sale — a flip
            earns no monthly income.
          </p>
        </div>
        <Select value={String(year)} onValueChange={(v) => onYearChange(Number(v))}>
          <SelectTrigger className="h-9 w-[110px]" aria-label="Year">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Deal economics (computeFlip) */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Tile
          label={isSold ? "Realized profit" : "Estimated profit"}
          value={(flip.profit < 0 ? "−" : "") + fmtMoney(Math.abs(flip.profit))}
          tone={flip.profit >= 0 ? "success" : "destructive"}
          sub="ARV − all-in costs"
          emphasize
        />
        <Tile
          label="ROI"
          value={fmtPct(flip.roi)}
          tone={flip.roi >= 0 ? "success" : "destructive"}
          sub="Profit ÷ cash invested"
        />
        <Tile
          label="Profit margin"
          value={fmtPct(flip.margin)}
          tone={flip.margin >= 15 ? "success" : flip.margin >= 0 ? undefined : "destructive"}
          sub="Profit ÷ ARV · target ≥ 15%"
        />
        <Tile
          label="Cash invested"
          value={fmtMoney(flip.cashInvested)}
          sub="Out-of-pocket in the deal"
        />
      </div>

      {/* Cost bands */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Tile label="Holding to date" value={fmtMoney(holdingToDate)} tone="warning" sub="Carry paid so far" />
        <Tile label="Projected holding" value={fmtMoney(flip.holding)} sub="Over the hold period" />
        <Tile label="Selling costs" value={fmtMoney(flip.selling)} tone="destructive" sub="Commission · transfer · closing" />
        <Tile label="Equity spread" value={fmtMoney(flip.equitySpread)} tone={flip.equitySpread >= 0 ? "success" : "destructive"} sub="ARV − (purchase + rehab)" />
      </div>

      {/* Sale event — only once sold */}
      {isSold && (
        <div className="rounded-xl border border-success/30 bg-success/5 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Sale event
          </div>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <SaleRow label="Sale proceeds" detail="≈ ARV" amount={arv} tone="success" />
            <SaleRow label="Selling costs" amount={-flip.selling} tone="destructive" />
            <SaleRow label="Loan payoff" amount={-loanPayoff} tone="destructive" />
            <SaleRow label="Net proceeds at close" amount={netProceeds} tone={netProceeds >= 0 ? "success" : "destructive"} strong />
          </div>
        </div>
      )}

      {/* Holding-cost timeline */}
      <div className="grid gap-5 lg:grid-cols-[210px_1fr]">
        <TimelineList months={months} selected={selected} onSelect={setSelected} />

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-foreground">
              {active.monthName} {active.year}
            </span>
            <div className="flex items-center gap-2">
              <PhaseBadge month={active} />
              {isOwned(active) && (
                <Button size="sm" variant="outline" onClick={() => onAddExpense(active)}>
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add carry cost
                </Button>
              )}
            </div>
          </div>
          <MonthCarryTable month={active} onViewBreakdown={onViewBreakdown} />
        </div>
      </div>
    </Card>
  );
}

/* ── Month timeline list ──────────────────────────────────────────────────── */
function TimelineList({
  months,
  selected,
  onSelect,
}: {
  months: YearMonthWithLogged[];
  selected: number;
  onSelect: (i: number) => void;
}) {
  let running = 0;
  return (
    <div className="flex flex-col gap-0.5">
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {months[0]?.year} carry by month
      </div>
      {months.map((m, i) => {
        const burn = monthBurn(m);
        if (isOwned(m)) running += burn;
        return (
          <button
            key={m.month}
            type="button"
            onClick={() => onSelect(i)}
            className={cn(
              // Fixed 210px track — a wide carry figure wraps under the month
              // instead of running past the row's border.
              "flex w-full flex-wrap items-center justify-between gap-x-2 gap-y-1 rounded-md border border-transparent px-3 py-2 text-left text-sm transition-colors hover:bg-muted",
              i === selected && "border-syntra-red/50 bg-syntra-red/10",
            )}
          >
            <span
              className={cn(
                "shrink-0",
                isOwned(m) ? "font-semibold text-foreground" : "font-medium text-muted-foreground",
              )}
            >
              {m.monthName.slice(0, 3)}
              {m.source === "live" && <span className="ml-1 text-[10px] text-muted-foreground">now</span>}
            </span>
            {m.rehabSpend > 0 ? (
              <span className="ml-auto whitespace-nowrap rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-semibold text-warning">
                −{fmtMoney(burn)}
              </span>
            ) : isOwned(m) ? (
              <span
                className={cn(
                  "ml-auto whitespace-nowrap font-semibold tabular-nums",
                  burn > 0.5 ? "text-destructive" : "text-muted-foreground",
                )}
              >
                {burn > 0.5 ? `−${fmtMoney(burn)}` : "—"}
              </span>
            ) : (
              <span className="ml-auto text-muted-foreground">—</span>
            )}
          </button>
        );
      })}
      <div className="mt-2 flex items-center justify-between rounded-md bg-muted/50 px-3 py-2 text-xs">
        <span className="font-semibold text-muted-foreground">Carry {months[0]?.year}</span>
        <span className="font-bold tabular-nums text-destructive">−{fmtMoney(running)}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <Legend dot="bg-warning" label="Rehab" />
        <Legend dot="bg-destructive" label="Hold / carry" />
        <Legend dot="bg-muted-foreground" label="Pre-acq." />
      </div>
    </div>
  );
}

function Legend({ dot, label }: { dot: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn("h-2 w-2 rounded-sm", dot)} /> {label}
    </span>
  );
}

/* ── Selected month carry table ───────────────────────────────────────────── */
function MonthCarryTable({
  month,
  onViewBreakdown,
}: {
  month: YearMonthWithLogged;
  onViewBreakdown: (month: YearMonthWithLogged) => void;
}) {
  if (!isOwned(month)) {
    const message =
      month.phase === "future"
        ? "This month hasn't happened yet."
        : month.phase === "sold"
          ? "This property was sold — no carry after the sale."
          : "This property wasn't owned yet this month.";
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        {message}
      </div>
    );
  }
  const debt = month.breakdown?.debt ?? 0;
  // Per-month holding (taxes+ins · utilities) already resolved into the
  // breakdown's opex — from the frozen log for past months, live for current.
  const holding = month.breakdown?.opex.total ?? 0;
  const burn = monthBurn(month);
  return (
    <div className="rounded-lg border border-border">
      <div className="grid grid-cols-[1fr_auto_auto] gap-3 border-b border-border px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        <span>Item</span>
        <span className="text-right">Detail</span>
        <span className="text-right">Amount</span>
      </div>

      <Section label="Carrying costs" />
      <Row item="Loan interest / payment" detail="acquisition loan" amount={`-${fmtMoney(debt)}`} tone="destructive" indent />
      {holding > 0 && (
        <Row item="Taxes, insurance & utilities" detail="monthly carry" amount={`-${fmtMoney(holding)}`} tone="destructive" indent />
      )}
      {month.loggedTotal > 0 && (
        <Row
          item="Logged carry"
          detail={`${month.logged.length} ${month.logged.length === 1 ? "entry" : "entries"} · taxes · ins · utils`}
          amount={`-${fmtMoney(month.loggedTotal)}`}
          tone="destructive"
          indent
        />
      )}

      {month.rehabSpend > 0 && (
        <>
          <Section label="Rehab" />
          <Row item="Rehab spend" detail="one-time capital" amount={`-${fmtMoney(month.rehabSpend)}`} tone="warning" />
        </>
      )}

      <Row item="Total this month" amount={`-${fmtMoney(burn)}`} tone="destructive" total />
      {(month.breakdown || month.loggedTotal > 0) && (
        <div className="border-t border-border p-2">
          <button
            type="button"
            onClick={() => onViewBreakdown(month)}
            className="flex w-full items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-muted"
          >
            View full breakdown
          </button>
        </div>
      )}
    </div>
  );
}

function Section({ label }: { label: string }) {
  return (
    <div className="bg-muted/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
  );
}

function Row({
  item,
  detail,
  amount,
  tone,
  indent,
  total,
}: {
  item: string;
  detail?: string;
  amount: string;
  tone?: "success" | "destructive" | "warning";
  indent?: boolean;
  total?: boolean;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-[1fr_auto_auto] items-center gap-3 border-b border-border px-4 py-2.5 text-sm last:border-b-0",
        total && "border-t-2 border-border bg-muted/60 font-semibold",
      )}
    >
      <span className={cn("text-foreground", indent && "pl-3 text-muted-foreground")}>{item}</span>
      <span className="text-right text-xs text-muted-foreground">{detail ?? ""}</span>
      <span
        className={cn(
          "text-right font-semibold tabular-nums",
          tone === "success" && "text-success",
          tone === "destructive" && "text-destructive",
          tone === "warning" && "text-warning",
        )}
      >
        {amount}
      </span>
    </div>
  );
}

function SaleRow({
  label,
  detail,
  amount,
  tone,
  strong,
}: {
  label: string;
  detail?: string;
  amount: number;
  tone?: "success" | "destructive";
  strong?: boolean;
}) {
  return (
    <div className={cn("flex items-center justify-between gap-3 text-sm", strong && "border-t border-success/20 pt-2 font-semibold sm:col-span-2")}>
      <span className="text-foreground">
        {label}
        {detail && <span className="ml-1 text-xs text-muted-foreground">{detail}</span>}
      </span>
      <span
        className={cn(
          "font-semibold tabular-nums",
          tone === "success" && "text-success",
          tone === "destructive" && "text-destructive",
        )}
      >
        {amount < 0 ? "−" : ""}
        {fmtMoney(Math.abs(amount))}
      </span>
    </div>
  );
}

function Tile({
  label,
  value,
  tone,
  sub,
  emphasize,
}: {
  label: string;
  value: string;
  tone?: "success" | "destructive" | "warning";
  sub?: string;
  emphasize?: boolean;
}) {
  return (
    <div className={cn("rounded-lg border border-border bg-muted/40 p-3", emphasize && "border-primary/20 bg-primary/5")}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={cn(
          "mt-1 text-lg font-bold tabular-nums text-foreground",
          tone === "success" && "text-success",
          tone === "destructive" && "text-destructive",
          tone === "warning" && "text-warning",
        )}
      >
        {value}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function PhaseBadge({ month }: { month: YearMonthWithLogged }) {
  if (month.phase === "rehab") {
    return <Badge className="bg-warning/15 text-warning hover:bg-warning/15">Rehab</Badge>;
  }
  if (month.phase === "hold") {
    return <Badge className="bg-destructive/15 text-destructive hover:bg-destructive/15">Holding</Badge>;
  }
  if (month.phase === "future") return <Badge variant="secondary">Future</Badge>;
  if (month.phase === "sold") return <Badge variant="secondary">Sold</Badge>;
  return <Badge variant="secondary">Not yet owned</Badge>;
}
